import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import { loadEcwid, getEcwidCart, syncEcwidCart } from "../lib/ecwid";
import { verificationStatus } from "../lib/api";
import { klaviyoIdentify, klaviyoStartedCheckout } from "../lib/klaviyo";
import { useCart } from "../context/CartContext";
import VerificationModal from "../components/VerificationModal";

export default function Checkout() {
  const { items } = useCart();
  const [phase, setPhase] = useState("loading");
  const [verifyOpen, setVerifyOpen] = useState(false);
  const location = useLocation();
  const targetPage = location.state?.openPage || "cart";

  useEffect(() => {
    if (targetPage === "account") return;
    const email = localStorage.getItem("fd_verify_email");
    if (!email) {
      setVerifyOpen(true);
      return;
    }
    verificationStatus(email)
      .then((st) => {
        if (!st?.verified) {
          localStorage.removeItem("fd_verify_email");
          localStorage.removeItem("fd_verify_name");
          setVerifyOpen(true);
        }
      })
      .catch(() => {});
  }, [targetPage]);

  useEffect(() => {
    const host = document.getElementById("ecwid-checkout");
    if (!host) return;
    const scrub = () => {
      host.querySelectorAll("a, span, div, p").forEach((el) => {
        const href = el.getAttribute("href") || "";
        if (href.includes("ecwid.com")) {
          el.style.display = "none";
          return;
        }
        if (el.children.length === 0 && /powered by ecwid|built on ecwid|online store is built/i.test(el.textContent || "")) {
          el.style.display = "none";
        }
      });
    };
    const obs = new MutationObserver(scrub);
    obs.observe(host, { childList: true, subtree: true });
    scrub();
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadEcwid()
      .then(async (Ecwid) => {
        if (items.length > 0) {
          try {
            const current = await getEcwidCart();
            const ecwidItems = current?.items || [];
            const matches =
              ecwidItems.length === items.length &&
              items.every((i) => ecwidItems.some((ci) => ci.product?.id === i.product_id && ci.quantity === i.qty));
            if (!matches) {
              await syncEcwidCart(
                items.map((i) => ({ id: i.product_id, quantity: i.qty, options: i.ecwid_options || undefined }))
              );
            }
          } catch {}
        }
        if (cancelled) return;
        try {
          Ecwid.OnAPILoaded.add(() => {
            Ecwid.OnSetProfile.add((customer) => {
              if (customer && customer.email) klaviyoIdentify(customer.email);
            });
            Ecwid.OnPageLoaded.add((page) => {
              const steps = ["CHECKOUT_ADDRESS", "CHECKOUT_DELIVERY", "CHECKOUT_PAYMENT_DETAILS"];
              if (!steps.includes(page.type)) return;
              getEcwidCart().then((cart) => {
                if (!cart) return;
                const cid = cart.cartId || "cart";
                const key = "kl_sc_" + cid;
                if (sessionStorage.getItem(key)) return;
                sessionStorage.setItem(key, "1");
                const kItems = (cart.items || []).map((ci) => ({
                  product_id: ci.product?.id,
                  sku: ci.product?.sku || "",
                  name: ci.product?.name || "Item",
                  image: ci.product?.imageUrl || ci.product?.thumbnailUrl || "",
                  qty: ci.quantity || 1,
                  unit_price: ci.product?.price || 0,
                  option: null,
                  categories: [],
                }));
                const total = Math.round(kItems.reduce((a, i) => a + i.unit_price * i.qty, 0) * 100) / 100;
                klaviyoStartedCheckout(cid, kItems, total);
              });
            });
          });
        } catch {}
        if (!window.__fdCheckoutMounted) {
          window.__fdCheckoutMounted = true;
          window._xnext_initialization_scripts = [
            { widgetType: "ProductBrowser", id: "ecwid-checkout", arg: ["id=ecwid-checkout", "defaultPage=cart"] },
          ];
          Ecwid.init();
          try {
            const targetTypes = targetPage === "account" ? ["ACCOUNT", "SIGN_IN"] : ["CART", "CHECKOUT"];
            let forced = false;
            Ecwid.OnPageLoaded.add((page) => {
              if (!forced && page && page.type && !targetTypes.includes(page.type)) {
                forced = true;
                Ecwid.openPage(targetPage);
              }
            });
          } catch {}
        }
        setPhase("ready");
      })
      .catch(() => setPhase("ready"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="mx-auto min-h-[70vh] max-w-5xl px-4 py-10 sm:px-6" data-testid="checkout-page">
      <Seo title="Secure Checkout | Farmacy District DC" description="Complete your Farmacy District DC order on our secure checkout." />
      <div className="mb-6 flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-brand-red" />
        <div>
          <h1 className="font-display text-2xl font-black tracking-tight sm:text-3xl">
            {targetPage === "account" ? "My Account" : "Secure Checkout"}
          </h1>
          <p className="text-xs text-neutral-500">Encrypted checkout · 21+ ID verified at delivery · I-71 compliant gifting</p>
        </div>
      </div>
      {phase !== "ready" && (
        <div className="flex items-center justify-center gap-3 py-24 text-sm font-semibold text-neutral-500" data-testid="checkout-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Preparing your order — one moment...
        </div>
      )}
      <div id="ecwid-checkout" className="min-h-[60vh]"></div>
      <p className="mt-6 text-center text-xs text-neutral-400">
        Questions while checking out? Call <a href="tel:+12029134523" className="font-bold text-brand-ink">(202) 913-4523</a> or{" "}
        <Link to="/shop" className="font-bold text-brand-ink underline-offset-2 hover:underline">continue shopping</Link>.
      </p>
      <VerificationModal
        open={verifyOpen}
        onClose={() => {
          setVerifyOpen(false);
          window.location.href = "/shop/all";
        }}
        items={items}
      />
    </main>
  );
}
